import PropTypes from 'prop-types'
import { forwardRef, memo, useCallback, useMemo, useState } from 'react'
import { normalizeSizeString } from 'app/util/sizes'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import usePressEvents from 'lib/react/hooks/usePressEvents'
import useTouchHoverEvents from 'lib/react/hooks/useTouchHoverEvents'
import { isTouch } from 'lib/support'
import classNames from 'lib/util/classNames'
import noop from 'lib/util/noop'
import { scrollIntoViewCenter } from 'lib/util/scroll'
import BorderHighlight from '../BorderHighlight/BorderHighlight'
import styles from './ButtonTab.module.scss'

/**
 * The `ButtonTab` represents a button intended
 * for use in a bar as a tab
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const ButtonTab = forwardRef((props, forwardedRef) => {
  const {
    Tag,
    children,
    className,
    color,
    index,
    isActive,
    isSelected,
    isTransitionActive,
    onClick,
    size,
    ...rest
  } = props
  const ref = useFallbackRef(forwardedRef)
  const [isHover, setIsHover] = useState(false)
  const isTouchHover = useTouchHoverEvents(ref)
  const isPressing = usePressEvents(ref)
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.button,
        styles[`color-${color}`],
        styles[`size-${normalizeSizeString(size)}`],
        isActive && styles.active,
        isTransitionActive && styles.transition,
        isTouchHover && styles.touch,
        isPressing && styles.pressing
      ]),
    [
      className,
      color,
      size,
      isActive,
      isTransitionActive,
      isPressing,
      isSelected,
      isTouchHover
    ]
  )
  const attributes = useMemo(
    () => ({
      role: 'tab',
      tabIndex: 0,
      style: {
        '--transition-delay-reveal': `${Math.min(0.3, index * 0.03)}s`
      },
      'aria-selected': isSelected
    }),
    [index, isSelected]
  )

  /*
const attributes = useMemo(
    () => ({
      className: `${styles.button} ${className}`.trimEnd(),
      role: 'tab',
      tabIndex: 0,
      'data-c': color,
      'data-sz': sizeStr,
      style: {
        '--transition-delay-reveal': `${Math.min(0.3, index * 0.03)}s`
      },
      ...conditionalProperties([
        [isActive, { 'data-a': '' }],
        [isTransitionActive, { 'data-ta': '' }],
        [isTouchHover, { 'data-t': '' }],
        [isPressing, { 'data-p': '' }],
        [isSelected, { 'aria-selected': true }]
      ])
    }),
    [
      isTouchHover,
      isActive,
      isTransitionActive,
      isPressing,
      isSelected,
      className,
      color,
      index,
      sizeStr
    ]
  )
  */

  /**
   * Handle clicking a tab, attempt to scroll it into center
   * then call the `onClick` handler
   */
  const handleClick = useCallback(
    e => {
      // Scroll it into view
      scrollIntoViewCenter(ref.current, 0.3, 0.3)

      // Callback
      onClick(e)
    },
    [onClick]
  )

  /**
   * Memo: The button background, no need to re-render
   */
  const buttonBg = useMemo(
    () => (
      <div className={styles.buttonBg} aria-hidden="true">
        <div className={styles.buttonBgOuter} />
        <div className={styles.buttonBgInner}>
          <BorderHighlight className={styles.highlight} isActive={isHover} />
        </div>
      </div>
    ),
    [isHover]
  )

  return (
    <Tag
      ref={ref}
      {...rest}
      {...attributes}
      className={classNameOutput}
      onClick={handleClick}
      onMouseEnter={() => {
        !isTouch && setIsHover(true)
      }}
      onMouseLeave={() => {
        !isTouch && setIsHover(false)
      }}
    >
      <div className={styles.buttonInner}>
        {buttonBg}
        <div className={styles.buttonContent}>{children}</div>
      </div>
    </Tag>
  )
})

ButtonTab.displayName = 'ButtonTab'

/** @type {object} */
ButtonTab.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.string,
  index: PropTypes.number,
  isSelected: PropTypes.bool,
  Tag: PropTypes.elementType,
  size: PropTypes.string,
  isActive: PropTypes.bool,
  isTransitionActive: PropTypes.bool,
  onClick: PropTypes.func
}

/** @type {object} */
ButtonTab.defaultProps = {
  children: '',
  className: '',
  color: 'a',
  index: 0,
  isSelected: false,
  Tag: 'button',
  size: 'standard',
  isActive: true,
  isTransitionActive: null,
  onClick: noop
}

// Memoize
export default memo(ButtonTab)
